
import { farmPriceService } from '../farmPriceService';
import { distributionCenterPriceService } from '../distributionCenterPriceService';
import { localShopPriceService } from '../localShopPriceService';
import { sundayMarketPriceService } from '../sundayMarketPriceService';
import { supermarketPriceService } from '../supermarketPriceService';
import { ecommercePriceService } from '../ecommercePriceService';
import { chipchipSalesService } from '../chipchip/salesDataService';
import { chipchipDatabaseService } from '../chipchip/chipchipDatabaseService';
import { BenchmarkFilters, PriceData } from './types';
import { getCanonicalProductName, getProductVariations } from './productNameMapping';

export async function fetchDataForLocationGroup(
  locationGroup: string, 
  filters: BenchmarkFilters
): Promise<PriceData[]> {
  try {
    let allData: any[] = [];
    
    // Get all variations of the product name for comprehensive matching
    const productVariations = getProductVariations(filters.product);
    
    // Fetch data for each product variation
    for (const productVariation of productVariations) {
      let data: any[] = [];
      
      // Call the appropriate method for each service with product variation
      switch (locationGroup) {
        case 'farm-prices':
          data = await farmPriceService.getFarmPrices({
            product_name: productVariation,
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
          });
          break;
        case 'distribution-center':
          data = await distributionCenterPriceService.getDistributionCenterPrices({
            product_name: productVariation,
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
          });
          break;
        case 'local-shops':
          data = await localShopPriceService.getLocalShopPrices({
            product_name: productVariation,
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
          });
          break;
        case 'sunday-market':
          data = await sundayMarketPriceService.getAll({
            product_name: productVariation,
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
          });
          break;
        case 'supermarket':
          data = await supermarketPriceService.getAll({
            product_name: productVariation,
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
          });
          break;
        case 'ecommerce':
          data = await ecommercePriceService.getAll({
            product_name: productVariation,
            date_from: filters.dateFrom,
            date_to: filters.dateTo,
          });
          break;
        case 'chipchip-sales':
          const chipchipData = await chipchipSalesService.fetchChipChipSalesData(
            productVariation,
            filters.dateFrom,
            filters.dateTo
          );
          data = chipchipSalesService.transformChipChipData(chipchipData);
          break;
        case 'chipchip-sales-db':
          const chipchipDbData = await chipchipDatabaseService.fetchChipChipSalesDataFromDB(
            productVariation,
            filters.dateFrom,
            filters.dateTo
          );
          data = chipchipDatabaseService.transformChipChipDatabaseData(chipchipDbData);
          break;
        default:
          continue;
      }
      
      allData.push(...data);
    }

    // Filter data by product variations and normalize to canonical name
    const filteredData = allData.filter(item => {
      if (!item.product_name) return false;
      const canonicalName = getCanonicalProductName(item.product_name);
      return canonicalName === filters.product;
    });

    const transformedData: PriceData[] = filteredData.map((item: any) => ({
      date: item.date,
      price: parseFloat(item.price),
      location: item.location || item.region || 'Unknown',
      product_name: filters.product, // Use canonical name
      source: locationGroup,
      origin: item.origin || undefined,
    }));

    return transformedData;
  } catch (error) {
    console.error(`Error fetching data for ${locationGroup}:`, error);
    return [];
  }
}
